import * as React from "react"
import { useTranslation } from "react-i18next"
import { Button, Icon, cn } from "@nexus/ui"
import {
  PERSONA_ONBOARDING_ANALYZE_MIN_MS,
  PERSONA_ONBOARDING_ANALYZE_STEP_MS,
  PERSONA_ONBOARDING_ANALYZE_STEPS,
} from "../../constants/personas.constants"

export interface PersonasOnboardingAnalyzingProps {
  /** True once the destination data has resolved (suggestions or empty library). */
  isDataReady: boolean
  isError?: boolean
  onReady: () => void
  onRetry?: () => void
  /** Active org label from the tenant switcher (e.g. "Taboola", "TPJ"). */
  organizationName?: string | null
}

type StepVisualStatus = "done" | "active" | "pending"

/** Figma analyzing beat — waits for data + a minimum duration before advancing. */
export function PersonasOnboardingAnalyzing({
  isDataReady,
  isError = false,
  onReady,
  onRetry,
  organizationName,
}: PersonasOnboardingAnalyzingProps) {
  const { t } = useTranslation()
  const [activeIndex, setActiveIndex] = React.useState(0)
  const [minElapsed, setMinElapsed] = React.useState(false)
  const advancedRef = React.useRef(false)

  React.useEffect(() => {
    const id = window.setTimeout(() => setMinElapsed(true), PERSONA_ONBOARDING_ANALYZE_MIN_MS)
    return () => window.clearTimeout(id)
  }, [])

  React.useEffect(() => {
    if (isError) return
    if (activeIndex >= PERSONA_ONBOARDING_ANALYZE_STEPS.length - 1) return
    const id = window.setTimeout(
      () => setActiveIndex((i) => i + 1),
      PERSONA_ONBOARDING_ANALYZE_STEP_MS,
    )
    return () => window.clearTimeout(id)
  }, [activeIndex, isError])

  React.useEffect(() => {
    if (advancedRef.current || isError || !isDataReady || !minElapsed) return
    advancedRef.current = true
    onReady()
  }, [isDataReady, isError, minElapsed, onReady])

  return (
    <section
      data-testid="personas-onboarding-analyzing"
      role="status"
      aria-live="polite"
      className={cn(
        "mx-auto flex w-full max-w-xl flex-col items-center gap-3.5 rounded-md border border-dashed border-border bg-card px-8 py-16 text-center sm:px-10 sm:py-[72px]",
        "motion-safe:animate-in motion-safe:fade-in motion-safe:duration-300 motion-safe:fill-mode-both motion-reduce:animate-none",
      )}
    >
      <div className="flex size-11 items-center justify-center rounded-full bg-primary/10">
        <Icon
          name={isError ? "alert-circle" : "loader"}
          size="sm"
          className={cn(
            isError ? "text-destructive" : "animate-spin text-primary motion-reduce:animate-none",
          )}
        />
      </div>

      <div className="flex max-w-md flex-col gap-1">
        <p className="text-[15px] font-semibold text-foreground">
          {isError
            ? t("personas.onboarding.analyzing.errorTitle", "Couldn't load brand personas")
            : t("personas.onboarding.analyzing.title", "Discovering personas from your brand…")}
        </p>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {isError
            ? t("personas.onboarding.analyzing.errorDescription", "Check your brand connection and try again.")
            : organizationName
              ? t("personas.onboarding.analyzing.subtitleWithOrganization", {
                  organization: organizationName,
                })
              : t("personas.onboarding.analyzing.subtitle", "Clustering tagged creatives by customer type.")}
        </p>
      </div>

      {!isError && (
        <ol className="mt-2 flex w-full min-w-[320px] max-w-xs flex-col gap-3 text-left">
          {PERSONA_ONBOARDING_ANALYZE_STEPS.map((step, i) => {
            const status: StepVisualStatus =
              i < activeIndex ? "done" : i === activeIndex ? "active" : "pending"
            return <AnalyzeStep key={step.id} label={t(step.labelKey)} status={status} />
          })}
        </ol>
      )}

      {isError && onRetry ? (
        <Button type="button" variant="secondary" size="sm" onClick={onRetry}>
          {t("personas.onboarding.analyzing.retry", "Try again")}
        </Button>
      ) : null}
    </section>
  )
}

function AnalyzeStep({ label, status }: { label: string; status: StepVisualStatus }) {
  return (
    <li className="flex items-center gap-2.5">
      <StepMark status={status} />
      <span
        className={cn(
          "text-[13px] leading-snug transition-colors duration-200 motion-reduce:transition-none",
          status === "pending" ? "text-muted-foreground" : "text-foreground",
          status === "active" && "font-medium",
        )}
      >
        {label}
      </span>
    </li>
  )
}

function StepMark({ status }: { status: StepVisualStatus }) {
  if (status === "done") {
    return (
      <span className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
        <Icon name="check" size="xs" color="inherit" />
      </span>
    )
  }
  if (status === "active") {
    return (
      <span className="flex size-[18px] shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
        <Icon name="loader" size="xs" className="animate-spin motion-reduce:animate-none" />
      </span>
    )
  }
  return <span className="size-[18px] shrink-0 rounded-full bg-muted" aria-hidden />
}
